import { useTheme } from '../ThemeContext';
import { useEffect, useState, useRef } from 'react';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer,  Legend } from 'recharts';

const MODELS = [
  { name:'ARIMA', acc:68.0, r2:0.42, mae:1.820, rmse:2.640, ram:12, speed:95, color:'#888888', desc:'Classical statistical time-series. Assumes stationarity — fails on 5G delay non-linearities.' },
  { name:'Pure RNN', acc:78.5, r2:0.61, mae:0.980, rmse:1.420, ram:85, speed:78, color:'#f0a500', desc:'Vanilla recurrent network. Vanishing gradient limits long-range dependency capture.' },
  { name:'LSTM Recursive', acc:88.2, r2:0.78, mae:0.520, rmse:0.890, ram:280, speed:52, color:'#0096dc', desc:'Strong baseline. Phase drift accumulates over sequences > 30 steps.' },
  { name:'LSTM Multi-Step', acc:82.4, r2:0.70, mae:0.680, rmse:1.050, ram:280, speed:60, color:'#9966ff', desc:'Predicts multiple steps simultaneously. Less drift but loses real-data grounding.' },
  { name:'Hybrid LSTM ✓', acc:94.68, r2:0.907, mae:0.201, rmse:0.441, ram:80, speed:88, color:'#00cc66', desc:'Block-wise recursive with real-data reset every 10 steps. Best across all metrics.' },
];

const RADAR_DATA = [
  { metric:'Accuracy', ARIMA:68, RNN:78, LSTM:88, 'Hybrid LSTM':95 },
  { metric:'R² Score', ARIMA:42, RNN:61, LSTM:78, 'Hybrid LSTM':91 },
  { metric:'Precision', ARIMA:55, RNN:70, LSTM:82, 'Hybrid LSTM':100 },
  { metric:'Low RAM', ARIMA:99, RNN:70, LSTM:38, 'Hybrid LSTM':93 },
  { metric:'Speed', ARIMA:95, RNN:78, LSTM:52, 'Hybrid LSTM':88 },
  { metric:'Drift Resist', ARIMA:30, RNN:40, LSTM:55, 'Hybrid LSTM':95 },
];



export default function ModelBenchmark() {
  const { dark } = useTheme();
  const [inView, setInView] = useState({});
  const [animBars, setAnimBars] = useState(false);
  const refs = useRef({});

  useEffect(() => {
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if(e.isIntersecting){ setInView(p=>({...p,[e.target.dataset.b]:true})); if(e.target.dataset.b==='bars') setAnimBars(true); } });
    }, {threshold:0.1});
    Object.values(refs.current).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const bg = dark?'#060c18':'#f4f8ff';
  const border = dark?'rgba(0,212,255,0.13)':'rgba(0,120,200,0.13)';
  const accent = dark?'#00d4ff':'#0078c8';
  const text1 = dark?'#e8f4ff':'#0a1e3d';
  const text2 = dark?'#8ab4d4':'#3a5a7a';
  const text3 = dark?'#4a7090':'#7090b0';
  const gridC = dark?'rgba(0,212,255,0.04)':'rgba(0,120,200,0.05)';
  const cardBg = dark?'#0d1929':'#ffffff';
  const cardShad = dark?'none':'0 2px 16px rgba(0,100,200,0.07)';
  const tickC = dark?'rgba(150,200,235,0.6)':'#888';
  const gridStroke = dark?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.06)';
  const ref = k => el => { refs.current[k]=el; };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Inter:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box;}
        .bm-root{background:${bg};color:${text1};font-family:'Inter',sans-serif;min-height:100vh;padding-top:60px;transition:background 0.35s;}
        .bm-root::before{content:'';position:fixed;inset:0;background-image:linear-gradient(${gridC} 1px,transparent 1px),linear-gradient(90deg,${gridC} 1px,transparent 1px);background-size:52px 52px;pointer-events:none;z-index:0;}
        .fade-in{opacity:0;transform:translateY(22px);transition:opacity 0.6s ease,transform 0.6s ease;}
        .fade-in.vis{opacity:1;transform:none;}
        .slabel{font-size:11px;font-weight:700;letter-spacing:3px;color:${text3};text-transform:uppercase;margin-bottom:16px;display:flex;align-items:center;gap:12px;}
        .slabel::after{content:'';flex:1;height:1px;background:${border};}
        .stitle{font-family:'Orbitron',monospace;font-size:clamp(20px,3vw,30px);font-weight:700;color:${text1};margin-bottom:10px;}
        .sbody{font-size:15px;color:${text2};line-height:1.8;margin-bottom:32px;max-width:660px;}

        /* comparison table */
        .ct{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:48px;}
        .ct th{font-size:10px;font-weight:700;letter-spacing:2px;color:${text3};text-align:left;padding:11px 14px;border-bottom:1px solid ${border};text-transform:uppercase;background:${dark?'rgba(0,212,255,0.03)':'rgba(0,120,200,0.03)'};}
        .ct td{padding:12px 14px;border-bottom:1px solid ${dark?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.04)'};color:${text2};vertical-align:middle;}
        .ct tr:hover td{background:${dark?'rgba(0,212,255,0.03)':'rgba(0,120,200,0.02)'};}
        .ct tr.best td{background:${dark?'rgba(0,204,102,0.05)':'rgba(0,160,80,0.03)'};}
        .ct tr.best td:first-child{border-left:2px solid #00cc66;}
        .win{color:#00cc66;font-weight:700;}
        .model-dot{display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:8px;}

        /* bar chart for accuracy */
        .bar-row{display:flex;flex-direction:column;gap:12px;margin-bottom:48px;}
        .bar-item{display:flex;align-items:center;gap:14px;}
        .bar-name{font-size:12px;font-weight:600;color:${text1};width:140px;flex-shrink:0;}
        .bar-track{flex:1;height:24px;background:${dark?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.05)'};border-radius:4px;overflow:hidden;position:relative;}
        .bar-fill{height:100%;border-radius:4px;transition:width 1.2s cubic-bezier(0.4,0,0.2,1);display:flex;align-items:center;padding-left:10px;}
        .bar-pct{font-size:11px;font-weight:700;color:#fff;font-family:'Orbitron',monospace;}
        .bar-val{font-size:12px;color:${text3};width:55px;text-align:right;flex-shrink:0;}
      `}</style>

      <div className="bm-root">
        {/* hero */}
        <div style={{position:'relative',zIndex:1,padding:'60px 24px 40px',textAlign:'center',borderBottom:`1px solid ${border}`,background:dark?'rgba(0,212,255,0.02)':'rgba(0,120,200,0.02)'}}>
          <div style={{background:dark?'rgba(0,212,255,0.07)':'rgba(0,120,200,0.07)',border:`1px solid ${border}`,display:'inline-block',padding:'5px 18px',borderRadius:20,marginBottom:20}}>
            <span style={{fontSize:11,fontWeight:600,letterSpacing:3,color:accent,textTransform:'uppercase'}}> Model Benchmark</span>
          </div>
          <h1 style={{fontFamily:"'Orbitron',monospace",fontSize:'clamp(24px,5vw,48px)',fontWeight:900,color:text1,marginBottom:12,lineHeight:1.08}}>
            Model Comparison<br/><span style={{color:accent}}>Why Hybrid LSTM Wins</span>
          </h1>
          <p style={{fontSize:16,color:text2,maxWidth:540,margin:'0 auto',lineHeight:1.7}}>
            Five approaches evaluated on identical TE1.csv test data. Hybrid LSTM outperforms on every critical metric.
          </p>
        </div>

        <div style={{position:'relative',zIndex:1,maxWidth:1060,margin:'0 auto',padding:'60px 24px 80px'}}>

          {/* Model cards */}
          <div data-b="mc" ref={ref('mc')}>
            <div className={`fade-in${inView.mc?' vis':''}`}>
              <div className="slabel"> 01 · Model Overview</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:14,marginBottom:48}}>
                {MODELS.map(m=>(
                  <div key={m.name} style={{border:`1px solid ${m.color}33`,background:cardBg,borderRadius:8,padding:18,boxShadow:cardShad,position:'relative',overflow:'hidden',transition:'all 0.25s'}}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=m.color+'88';e.currentTarget.style.transform='translateY(-3px)';}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=m.color+'33';e.currentTarget.style.transform='none';}}
                  >
                    <div style={{position:'absolute',top:0,left:0,right:0,height:2,background:m.color}}/>
                    <div style={{fontFamily:"'Orbitron',monospace",fontSize:12,fontWeight:700,color:m.color,marginBottom:6}}>{m.name}</div>
                    <div style={{fontSize:28,fontWeight:900,color:text1,fontFamily:"'Orbitron',monospace",marginBottom:4}}>{m.acc}%</div>
                    <div style={{fontSize:11,color:text3,marginBottom:10}}>Accuracy</div>
                    <div style={{fontSize:12,color:text2,lineHeight:1.5}}>{m.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Accuracy bars */}
          <div data-b="bars" ref={ref('bars')}>
            <div className={`fade-in${inView.bars?' vis':''}`}>
              <div className="slabel"> 02 · Accuracy Comparison</div>
              <div className="stitle">Performance on TE1.csv</div>
              <div className="bar-row">
                {MODELS.map((m,i)=>(
                  <div className="bar-item" key={m.name}>
                    <div className="bar-name">
                      <span className="model-dot" style={{background:m.color}}/>
                      {m.name}
                    </div>
                    <div className="bar-track">
                      <div className="bar-fill" style={{width:animBars?m.acc+'%':'0%',background:m.color,transitionDelay:`${i*0.12}s`}}>
                        <span className="bar-pct">{m.acc}%</span>
                      </div>
                    </div>
                    <div className="bar-val">R²={m.r2}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Full metrics table */}
          <div data-b="tbl" ref={ref('tbl')}>
            <div className={`fade-in${inView.tbl?' vis':''}`}>
              <div className="slabel"> 03 · Full Metrics Table</div>
              <div style={{overflow:'auto',marginBottom:48}}>
                <table className="ct">
                  <thead>
                    <tr>
                      <th>Model</th><th>Accuracy</th><th>R² Score</th>
                      <th>MAE (ms)</th><th>RMSE (ms)</th><th>RAM (MB)</th><th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MODELS.map(m=>(
                      <tr key={m.name} className={m.name.includes('Hybrid')?'best':''}>
                        <td><span className="model-dot" style={{background:m.color}}/><strong style={{color:text1}}>{m.name}</strong></td>
                        <td className={m.name.includes('Hybrid')?'win':''}>{m.acc}%</td>
                        <td className={m.name.includes('Hybrid')?'win':''}>{m.r2}</td>
                        <td className={m.name.includes('Hybrid')?'win':''}>{m.mae}</td>
                        <td className={m.name.includes('Hybrid')?'win':''}>{m.rmse}</td>
                        <td className={m.name.includes('Hybrid')?'win':''}>{m.ram} MB</td>
                        <td style={{fontSize:12}}>{m.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Radar chart */}
          <div data-b="radar" ref={ref('radar')}>
            <div className={`fade-in${inView.radar?' vis':''}`}>
              <div className="slabel"> 04 · Multi-Dimensional Radar</div>
              <div className="stitle">Balanced Performance Profile</div>
              <p className="sbody">Hybrid LSTM is the only model that excels across all six dimensions simultaneously — accuracy, precision, drift resistance, RAM efficiency, and speed.</p>
              <div style={{border:`1px solid ${border}`,background:cardBg,borderRadius:8,padding:24,boxShadow:cardShad}}>
                <ResponsiveContainer width="100%" height={380}>
                  <RadarChart data={RADAR_DATA}>
                    <PolarGrid stroke={gridStroke}/>
                    <PolarAngleAxis dataKey="metric" tick={{fill:tickC,fontSize:12,fontFamily:'Inter'}}/>
                    <Radar name="ARIMA" dataKey="ARIMA" stroke="#888" fill="#888" fillOpacity={0.1}/>
                    <Radar name="LSTM" dataKey="LSTM" stroke="#0096dc" fill="#0096dc" fillOpacity={0.1}/>
                    <Radar name="Hybrid LSTM" dataKey="Hybrid LSTM" stroke="#00cc66" fill="#00cc66" fillOpacity={0.18}/>
                    <Legend wrapperStyle={{fontFamily:'Inter',fontSize:12,color:text2}}/>
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Key insight */}
          <div data-b="insight" ref={ref('insight')} style={{marginTop:48}}>
            <div className={`fade-in${inView.insight?' vis':''}`}>
              <div className="slabel"> 05 · Key Insight</div>
              <div style={{border:`1px solid rgba(0,204,102,0.3)`,background:dark?'rgba(0,204,102,0.05)':'rgba(0,160,80,0.04)',borderRadius:8,padding:28,boxShadow:cardShad}}>
                <div style={{fontFamily:"'Orbitron',monospace",fontSize:16,fontWeight:700,color:'#00cc66',marginBottom:12}}>Why Hybrid Beats Pure Recursive</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
                  <div>
                    <div style={{fontWeight:600,color:text1,marginBottom:8,fontSize:14}}>Pure Recursive LSTM Problem:</div>
                    <div style={{fontSize:13,color:text2,lineHeight:1.7}}>Error accumulates as O(n) — each predicted value fed back compounds the error. By step 50, the signal has drifted completely from reality. Phase shift makes it useless for long-horizon forecasting.</div>
                  </div>
                  <div>
                    <div style={{fontWeight:600,color:'#00cc66',marginBottom:8,fontSize:14}}>Hybrid Solution:</div>
                    <div style={{fontSize:13,color:text2,lineHeight:1.7}}>Error is bounded to O(10) — predict a block of 10 future values, then reset context with real observed data. Drift never exceeds 10 steps. This is why R² stays at 0.91 regardless of total sequence length.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
